import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Linking a CG order to a project, on purpose.
 *
 * ── Why this is a deliberate action and not a side effect ──────────────────
 *
 * Signing off a CG order used to create a project every time. That filled the
 * Projects tab with records nobody asked for, and it was removed before this
 * work started — `noAutoProject.test.ts` reads every migration in order and
 * keeps it removed.
 *
 * Kyu: "Order = fulfillment/commercial workflow. Project = internal
 * work/deliverables workflow. Sometimes one order needs one project. Sometimes
 * an order needs none."
 *
 * So a project exists for an order when somebody says it should.
 *
 * ── The safety rule these hooks exist to carry ─────────────────────────────
 *
 * Both calls go through security-definer functions rather than updating
 * cg_orders from here, and that is not ceremony. `cg_order_owner()` resolves
 * an order's owner as coalesce(owner_id, created_by, project.owner_id) — so
 * for an order with NEITHER an owner nor a creator, linking a project would
 * silently hand ownership to whoever owns that project, and with it eight
 * kinds of notification: status changes, revision requests, PO revisions,
 * stall alerts and sign-offs.
 *
 * Measured on the live data before building: 4 orders are in exactly that
 * state. So the function sets owner_id explicitly first, naming the person who
 * clicked. Putting that in the database rather than here means it cannot be
 * forgotten by a future caller.
 */

export interface LinkedProject {
  id: string;
  name: string;
}

/** Projects that no order has claimed yet, for the picker. */
export async function fetchLinkableProjects(client: SupabaseClient): Promise<LinkedProject[]> {
  const { data, error } = await client
    .from('projects')
    .select('id, name')
    // The holder project for CG gate rows is archived and must never be
    // offered — it is infrastructure, not somebody's project.
    .eq('is_archived', false)
    // Rooms (Drop 3a) are projects with no tasks or board; offering one here
    // would let an order attach itself to a room, which is nonsensical
    // (plan-drop3-dms-groups.md decision D7).
    .eq('is_room', false)
    .order('name');
  if (error) {
    throw new Error(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useLinkableProjects(enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ['cg', 'linkable-projects'] as const,
    queryFn: () => fetchLinkableProjects(client),
    enabled,
    retry: false,
  });
}

export function useCreateProjectForOrder() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; name?: string }): Promise<string> => {
      const { data, error } = await client.rpc('create_project_for_order', {
        p_order: input.orderId,
        // exactOptionalPropertyTypes: the argument is omitted rather than sent
        // as null, so the function's own default supplies the name.
        ...(input.name === undefined ? {} : { p_name: input.name }),
      });
      if (error) {
        throw new Error(error.message);
      }
      return typeof data === 'string' ? data : '';
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['cg'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useLinkOrderToProject() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; projectId: string }): Promise<void> => {
      const { error } = await client.rpc('link_order_to_project', {
        p_order: input.orderId,
        p_project: input.projectId,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['cg'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/** The database's refusals, in words somebody can act on. */
export function linkErrorMessage(raw: string): string {
  if (raw.includes('order_already_linked')) {
    return 'This order already has a linked project. Open that one, or unlink it first.';
  }
  if (raw.includes('project_already_linked')) {
    return 'That project already belongs to another order. Pick a different one.';
  }
  if (raw.includes('not_a_cg_member')) {
    return 'CG Orders is limited to the CG team.';
  }
  if (raw.includes('order_not_found') || raw.includes('project_not_found')) {
    return 'That record could not be found. Refresh and try again.';
  }
  return 'That could not be saved. Try again.';
}
